/**
 * Parses AI message content that may contain inline <quiz> blocks.
 *
 * The AI emits quiz blocks in the following format:
 *
 *   <quiz>
 *   {"type":"multiple_choice","question":"...","options":[...],"correctIndex":0,"explanation":"..."}
 *   </quiz>
 *
 * This parser splits the content into an ordered array of text segments and
 * parsed quiz data objects. During streaming, an incomplete quiz block (opening
 * marker found but no closing marker yet) is represented as a `loading` segment.
 */

export interface MultipleChoiceQuiz {
  type: 'multiple_choice';
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface FillInBlankQuiz {
  type: 'fill_in_blank';
  question: string;
  answer: string;
  acceptableAnswers: string[];
  explanation: string;
}

export type QuizData = MultipleChoiceQuiz | FillInBlankQuiz;

export type ContentSegment =
  | { type: 'text'; content: string }
  | { type: 'quiz'; data: QuizData }
  | { type: 'loading' };

const QUIZ_OPEN = '<quiz>';
const QUIZ_CLOSE = '</quiz>';

export function parseMessageContent(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let remaining = content;

  while (remaining.length > 0) {
    const openIdx = remaining.indexOf(QUIZ_OPEN);

    if (openIdx === -1) {
      // No more quiz blocks — rest is plain text
      if (remaining.trim()) {
        segments.push({ type: 'text', content: remaining });
      }
      break;
    }

    // Text before the quiz block
    const textBefore = remaining.slice(0, openIdx);
    if (textBefore.trim()) {
      segments.push({ type: 'text', content: textBefore });
    }

    // Advance past the opening marker
    const afterOpen = remaining.slice(openIdx + QUIZ_OPEN.length);

    // Look for closing marker
    const closeIdx = afterOpen.indexOf(QUIZ_CLOSE);

    if (closeIdx === -1) {
      // Incomplete block (still streaming) — show loading indicator
      segments.push({ type: 'loading' });
      break;
    }

    // Extract JSON between markers
    const jsonStr = afterOpen.slice(0, closeIdx).trim();
    try {
      const parsed = JSON.parse(jsonStr) as QuizData;
      if (parsed.type === 'multiple_choice' || parsed.type === 'fill_in_blank') {
        segments.push({ type: 'quiz', data: parsed });
      } else {
        // Unknown quiz type — render as text
        segments.push({ type: 'text', content: jsonStr });
      }
    } catch {
      // Invalid JSON — render raw content as text
      segments.push({ type: 'text', content: jsonStr });
    }

    // Advance past closing marker
    remaining = afterOpen.slice(closeIdx + QUIZ_CLOSE.length);
  }

  return segments;
}
